import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import BookingDto from '@/models/Bookings/BookingDto';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import BookingSearchViewModel from '@/models/Bookings/BookingSearchViewModel';
import BookingSearchRequest from '@/models/Bookings/BookingSearchRequest';
import LocationProfileSearchRequest from '@/models/Locations/LocationProfileSearchRequest';
import LocationProfileDto from '@/models/Locations/LocationProfileDto';
import CompanyProfileDto from '@/models/Companies/CompanyProfileDto';
import CompanyProfileSearchRequest from '@/models/Companies/CompanyProfileSearchRequest';
import CreateBookingRequest from '@/models/Bookings/CreateBookingRequest';

export interface IBookingListState {
  loading: boolean;
  bookings: BookingDto[];
  bookingSearchViewModel: BookingSearchViewModel;
  newBookingId: string;
  loadingDialog: boolean;
}

@Module({ dynamic: true, store, name: 'BookingList', namespaced: true})
class BookingList extends VuexModule implements IBookingListState {
  
  public loading = false;
  public bookings: BookingDto[] = [];
  public bookingSearchViewModel: BookingSearchViewModel = new BookingSearchViewModel();
  public newBookingId = '';
  public loadingDialog = false;

  @Action
  async searchBookings() {
    const request = new BookingSearchRequest();

    if (this.bookingSearchViewModel.selectedBranch != null) {
      request.branchId = this.bookingSearchViewModel.selectedBranch.id;
    }

    request.startAtFrom = this.bookingSearchViewModel.startAtFrom;

    if (this.bookingSearchViewModel.selectedCompany != null) {
      request.companyId = this.bookingSearchViewModel.selectedCompany.id;
    }

    if (this.bookingSearchViewModel.selectedLocation != null) {
      request.locationId = this.bookingSearchViewModel.selectedLocation.id;
    }

    request.status = this.bookingSearchViewModel.selectedStatus;

    this.setLoading(true);
    await ajax.post(`/api/v1/agency/bookings/search`, request)
    .then(response => {
      this.context.commit("setBookings", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action
  async getCompaniesForSearchOptions() {
    const request = new CompanyProfileSearchRequest();
    await ajax.post(`/api/v1/agency/companies/search`, request)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setCompanies", response.data);
    })
    .catch(error => {
      AppModule.handleApiError(error);
    });
  }

  @Action
  async getLocationsForSearchOptions() {
    const request = new LocationProfileSearchRequest();
    await ajax.post(`/api/v1/agency/locations/search`, request)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setLocations", response.data);
    })
    .catch(error => {
      AppModule.handleApiError(error);
    });
  }

  @Action
  public async createBooking(request: CreateBookingRequest) {
    this.setLoading(true); 
    await ajax.post(`/api/v1/agency/bookings`, request)
    .then(response => {
      this.context.commit("setBookingIdFromCreate", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
	  this.setLoading(false);
    })
  }

  @Action
  cleanState() {
    this.context.commit("resetState");
  }

  @Mutation
  resetState() {
    this.bookings = [];
    this.newBookingId = '';
    this.bookingSearchViewModel = new BookingSearchViewModel();
    this.loading = false;
    this.loadingDialog = false;
  }

  @Mutation
  private async setLoading(isLoading: boolean) {
      this.loading = isLoading;
  }

  @Mutation
  private async setLoadingDialog(isLoadingDialog: boolean) {
      this.loadingDialog = isLoadingDialog;
  }

  @Mutation
  private async setCompanies(companies: CompanyProfileDto[]) {
      this.bookingSearchViewModel.companies = companies;
  }

  @Mutation
  private async setLocations(locations: LocationProfileDto[]) {
      this.bookingSearchViewModel.locations = locations;
  }

  @Mutation
  private async setBookings(bookings: BookingDto[]) {
      this.bookings = bookings;
  }

  @Mutation
  private async setBookingIdFromCreate(booking: BookingDto) {
    this.newBookingId = booking.id;
  }
}

export const BookingListModule = getModule(BookingList)
